import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Avatar,
  Box,
  Typography,
  Grid,
  Link as MuiLink,
} from '@material-ui/core';
import { Block, CheckCircleOutline, DeleteOutline, Security, Timer } from '@material-ui/icons';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { IImpersonationDashboard, IImpersonationRequest, PropertyError } from '../../../../Api/AptorApi';
import { useAptorApi } from '../../../../Api';
import { useApiValidationSnackbar } from '../../../../Api/useValidationSnackbar';
import { useStyles } from './ImpersonationRequestsWidget.styles';

const emptyDashboard: IImpersonationDashboard = { receivedRequests: [] };
const reasonPreviewLength = 100;

interface IImpersonationRequestsWidgetProps {
  requestId?: string;
  decision?: 'accept' | 'reject';
}

export const ImpersonationRequestsWidget = ({ requestId, decision }: IImpersonationRequestsWidgetProps) => {
  const classes = useStyles();
  const { api, abortController } = useAptorApi();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { notifyUnhandledException } = useApiValidationSnackbar();
  const [dashboard, setDashboard] = useState<IImpersonationDashboard>(emptyDashboard);
  const [respondingId, setRespondingId] = useState<string>();
  const [now, setNow] = useState(Date.now());
  const [expandedReasonIds, setExpandedReasonIds] = useState<string[]>([]);

  useEffect(() => {
    if (!requestId || !decision) return;
    document.getElementById(`impersonation-request-${requestId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [requestId, decision, dashboard.receivedRequests]);

  const load = useCallback(async () => {
    const result = await api.getImpersonationDashboard();
    if (!abortController.current.signal.aborted) {
      setDashboard({ receivedRequests: result.receivedRequests || [] });
    }
  }, [api, abortController]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  const notifyValidationErrors = (errors: PropertyError[]) => {
    errors.forEach((propertyError) => {
      propertyError.errors.forEach((error) => enqueueSnackbar(error.message, { variant: 'error' }));
    });
  };

  const respond = async (request: IImpersonationRequest, accept: boolean) => {
    setRespondingId(request.id);
    try {
      let responded = false;
      await api.invoke(
        async (client) => {
          if (accept) {
            await client.acceptImpersonationRequest(request.id);
          } else {
            await client.rejectImpersonationRequest(request.id);
          }
          responded = true;
        },
        abortController.current,
        notifyValidationErrors,
        notifyUnhandledException,
      );
      if (!responded) {
        await load();
        return;
      }
      enqueueSnackbar(
        formatMessage({
          id: accept ? 'dashboard.impersonation.accepted' : 'dashboard.impersonation.rejected',
        }),
        { variant: accept ? 'success' : 'error' },
      );
      await load();
      if (request.id.toLowerCase() === requestId?.toLowerCase()) {
        const query = new URLSearchParams(window.location.search);
        query.delete('requestId');
        query.delete('decision');
        window.history.replaceState({}, '', `${window.location.pathname}${query.toString() ? `?${query.toString()}` : ''}`);
      }
    } catch {
      notifyUnhandledException();
      await load().catch(() => undefined);
    } finally {
      setRespondingId(undefined);
    }
  };

  const deleteRequest = async (request: IImpersonationRequest) => {
    setRespondingId(request.id);
    try {
      let deleted = false;
      await api.invoke(
        async (client) => {
          await client.deleteImpersonationRequest(request.id);
          deleted = true;
        },
        abortController.current,
        notifyValidationErrors,
        notifyUnhandledException,
      );
      if (!deleted) {
        await load();
        return;
      }
      enqueueSnackbar(formatMessage({ id: 'dashboard.impersonation.dismissed' }), { variant: 'success' });
      await load();
    } catch {
      notifyUnhandledException();
      await load().catch(() => undefined);
    } finally {
      setRespondingId(undefined);
    }
  };

  const formatTimeLeft = (respondBeforeUTC: Date) => {
    const milliseconds = Math.max(moment.utc(respondBeforeUTC).valueOf() - now, 0);
    const duration = moment.duration(milliseconds);
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const received = dashboard.receivedRequests;

  if (received.length === 0) {
    return null;
  }

  return (
    <Grid item className={classes.widgetGridItem}>
      <Box className={classes.requestList}>
        {received.map((request) => {
          const isLongReason = (request.reason?.length ?? 0) > reasonPreviewLength;
          const isReasonExpanded = expandedReasonIds.includes(request.id);
          const isExpired = moment.utc(request.respondBeforeUTC).valueOf() <= now ||
            (!!request.requestedAccessValidToUTC && moment.utc(request.requestedAccessValidToUTC).valueOf() <= now);
          return (
          <Card id={`impersonation-request-${request.id}`} key={request.id} className={classes.requestCard}>
            <CardHeader
              className={classes.cardHeader}
              avatar={<Avatar className={classes.avatar}><Security /></Avatar>}
              title={
                <Box className={classes.headingRow}>
                  <Typography variant="h4" className={classes.heading}>{formatMessage({ id: 'dashboard.impersonation.invite-title' })}</Typography>
                  <Chip
                    size="small"
                    label={formatMessage({
                      id: request.status === 'pending' && isExpired
                        ? 'admin.impersonations.filter.expired'
                        : 'dashboard.impersonation.pending',
                    })}
                    className={
                      isExpired
                        ? classes.expiredChip
                        : classes.pendingChip
                    }
                  />
                </Box>
              }
              action={request.status === 'pending' ? (
                <Chip
                  size="small"
                  icon={<Timer />}
                  label={formatTimeLeft(request.respondBeforeUTC)}
                  className={classes.timer}
                />
              ) : undefined}
            />
            <CardContent className={classes.requestContent}>
              <Typography variant="body2">{formatMessage({ id: 'dashboard.impersonation.requested-by' })}: {request.requestedByName}</Typography>
              {request.reason && (
                <Box className={classes.reasonRow}>
                  <Typography variant="body2" className={classes.reasonLabel}>
                    {formatMessage({ id: 'dashboard.impersonation.reason' })}:
                  </Typography>
                  <Box className={isReasonExpanded ? classes.reasonValueExpanded : classes.reasonValue}>
                    <Typography component="span" variant="body2" className={isReasonExpanded ? classes.reasonExpanded : classes.reasonPreview}>
                      {request.reason}
                    </Typography>
                    {isLongReason && (
                      <MuiLink
                        className={classes.showMoreLink}
                        component="button"
                        type="button"
                        onClick={() => setExpandedReasonIds((current) => (
                          isReasonExpanded
                            ? current.filter((id) => id !== request.id)
                            : [...current, request.id]
                        ))}
                      >
                        {formatMessage({ id: isReasonExpanded ? 'utils.show-less' : 'utils.show-more' })}
                      </MuiLink>
                    )}
                  </Box>
                </Box>
              )}
              <Typography variant="body2" color="textSecondary">
                {formatMessage({ id: 'dashboard.impersonation.respond-before' })}: {moment.utc(request.respondBeforeUTC).local().format('lll')}
              </Typography>
              {request.requestedAccessValidToUTC && (
                <Typography variant="body2" color="textSecondary">
                  {formatMessage({ id: 'admin.impersonation-request.access-end' })}: {moment.utc(request.requestedAccessValidToUTC).local().format('lll')}
                </Typography>
              )}
            </CardContent>
            <CardActions className={classes.actions}>
              {isExpired ? (
                <Button className={classes.deleteButton} variant="contained" startIcon={<DeleteOutline />} disabled={!!respondingId} onClick={() => deleteRequest(request)}>
                  {formatMessage({ id: 'form.delete' })}
                </Button>
              ) : (
                <>
                  <Button className={classes.acceptButton} variant="contained" startIcon={<CheckCircleOutline />} disabled={!!respondingId} onClick={() => respond(request, true)}>
                    {formatMessage({ id: request.id.toLowerCase() === requestId?.toLowerCase() && decision === 'accept' ? 'impersonation.response.confirm-accept' : 'dashboard.impersonation.accept' })}
                  </Button>
                  <Button className={classes.rejectButton} variant="contained" startIcon={<Block />} disabled={!!respondingId} onClick={() => respond(request, false)}>
                    {formatMessage({ id: request.id.toLowerCase() === requestId?.toLowerCase() && decision === 'reject' ? 'impersonation.response.confirm-reject' : 'dashboard.impersonation.reject' })}
                  </Button>
                </>
              )}
            </CardActions>
          </Card>
          );
        })}
      </Box>
    </Grid>
  );
};
